import { PaymentStatus } from './../../models/payment-status.enum';
import { tap, finalize, catchError, take } from 'rxjs/operators';
import {
  Component,
  OnInit,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges, NgZone
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators
} from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { OnyxEvent } from '../../models/event';
import { PaymentStore } from '../../service/payment.store';
import { AdjustmentStore } from '../../service/adjustment.store';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { LogService } from '../../../shared/log/log.service';
import { PioneerValidators } from '../../../shared/utils/pioneer.validators';
import { Payment } from '../../models/payment';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { EventStore } from '../../service/event.store';
import { EventUtils } from '../../utils/event-utils';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { PaymentStatusUtils } from '../../utils/payment-status-utils';
import { PaymentType } from '../../models/payment-type';
import { DateUtil } from 'app/shared/utils/date.util';
import { NonCommissionType } from 'app/event/models/non-commission-type';
import { DropdownItem } from 'app/event/models/dropdown-item';
import { MenuStore } from 'app/menus/service/menu.store';
import { ThrowStmt } from '@angular/compiler';
import { Adjustment } from 'app/event/models/adjustment';
import { List } from 'immutable';

@Component({
  selector: 'pioneer-add-payment',
  templateUrl: './add-payment.component.html'
})
export class AddPaymentComponent implements OnInit, OnChanges {

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  paymentForm: FormGroup;
  adjustmentForm: FormGroup;
  eventForm: FormGroup;

  addPaymentEnabled = true;
  isEventFinished = false;
  finalPaymentHint: string;

  isPropertyUser: boolean;

  confirmationModalConfig = new ConfirmationModalConfig();

  onyxEvent: OnyxEvent;

  nonCommissionTypes: DropdownItem[];
  cancelTypes: DropdownItem[];
  paidDirectTypes: DropdownItem[];

  public strikeThroughPrepayment = false;

  isPropertySubscribed: boolean;

  @Input()
  public prepayment: Payment = null;

  private latestFinalPayment: Payment = null;

  hasPayment: boolean;
  isLastPaymentRequestedOrRequestResentOrRequestRejected: boolean;
  hasAdjustments: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private paymentStore: PaymentStore,
    private adjustmentStore: AdjustmentStore,
    private translateService: TranslateService,
    private environment: EnvironmentConfiguration,
    private errorHandlerService: ErrorHandlerService,
    private logService: LogService,
    private decimalPipe: DecimalPipe,
    private uppercasePipe: UpperCasePipe,
    private eventStore: EventStore,
    private authenticationStore: AuthenticationStore,
    private menuStore: MenuStore,
    public ngZone: NgZone
  ) {}

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {
    this.isPropertyUser = this.authenticationStore.isPropertyUser();

    this.paymentForm = this.formBuilder.group({
      'description': [ '', Validators.maxLength(255) ],
      'type': [ null, ],
      'cancelType': [ null, ],
      'commissionAmount': [ null, [ PioneerValidators.isValidAmount ]]
    });

    this.adjustmentForm = this.formBuilder.group({
      // defaults to undefined so that someone is required to enter an amount before submitting
      'adjustmentAmount': [ undefined, [ Validators.required, PioneerValidators.isValidAmount ] ]
    });

    this.eventForm = this.formBuilder.group({
      'masterBillPaid': [ '',  ]
    });

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'AddPaymentComponent'
        })
      );
    }

    if (this.prepayment) {
      if (PaymentStatusUtils.isError(this.prepayment.status)) {
        this.strikeThroughPrepayment = true;
      }
    }

    this.checkEventDates();

    this.retrieveFinalPaymentHint();

    this.paymentStore.getLatestFinalPayment()
      .take(1)
      .subscribe((latestFinalPayment: Payment) => this.latestFinalPayment = latestFinalPayment);

    if (this.prepayment) {
      this.hasPayment = true;
    }

    this.paymentStore.payments.take(1).subscribe(paymentList => {
      if (paymentList.size > 0) {
        this.hasPayment = true;
      }
    });

    this.menuStore.isPropertySubscribed.subscribe(propertySubscribed => {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.isPropertySubscribed = propertySubscribed;
          });
        }, 0);
      });

    });

    this.addNonCommissionDueTypes();
    this.addCancelTypes();
    this.addPaidDirectTypes();

    this.paymentStore.isLastPaymentRequestedOrRequestResentOrRequestRejected.subscribe(isReqOrReqResentOrRej => {
      this.isLastPaymentRequestedOrRequestResentOrRequestRejected = isReqOrReqResentOrRej;
    });

    this.areThereAnyAdjustments().subscribe( hasAdjustments => {
      if (hasAdjustments) {
        this.hasAdjustments = true;
      }
    });

  }

  ngOnChanges(changes: SimpleChanges): void {

    if (!changes[ 'event' ]) {
      return;
    }
  }

  isValidPaymentDate(): boolean {
    if (!this.onyxEvent || !this.onyxEvent.endDate) {
      return false;
    }

    const firstPayDate = new Date(this.onyxEvent.endDate);
    firstPayDate.setDate(firstPayDate.getDate() + this.environment.payments.paymentDelayDaysAfterEventEnd + 1);

    const firstPayDateUtcSeconds = firstPayDate.getTime();
    const currentUtcSeconds = new Date().getTime();
    const isValid = currentUtcSeconds >= firstPayDateUtcSeconds;

    this.logService.verbose(`allow: ${firstPayDateUtcSeconds}, current: ${currentUtcSeconds}, isValid: ${isValid}`);

    return isValid;
  }

  confirmBeforeAuthorizingPayment(): void {
    // set up the title of the confirmation modal
    this.calculatedPayment.map((calculatedPayment: number) => {
      const label = this.showRequestPayment()
        ? 'EVENT.COMMISSION.REQUEST_PAYMENT_CONFIRMATION_TITLE' : 'EVENT.COMMISSION.AUTHORIZE_PAYMENT_CONFIRMATION_TITLE';
      const confirmLabel = this.showRequestPayment()
        ? 'EVENT.COMMISSION.CONFIRMATION_REQUEST' : 'EVENT.COMMISSION.CONFIRMATION_CONFIRM';
      const params = {
        paymentAmount: this.decimalPipe.transform(calculatedPayment, '1.2-2'),
        currency: this.uppercasePipe.transform(this.onyxEvent.currency)
       };
      const title = this.translateService.instant(label, params);
      const confirmTitle = this.translateService.instant(confirmLabel);
      this.confirmationModalConfig.title = title;
      this.confirmationModalConfig.confirm = confirmTitle;
    }).subscribe();

    this.confirmationModal.open();
  }

  savePayment(): Observable<any> {
    const obs = this.calculatedPayment.first().flatMap((commissionAmount: number) => {
      const description = this.paymentForm.value.description;
      const payment = new Payment({
        commissionAmount: commissionAmount,
        description: description,
        commissionCurrency: this.onyxEvent.currency,
        eventId: this.onyxEvent.eventId,
        type: PaymentType.FINAL_PAYMENT
      });

      this.addPaymentEnabled = false;
      if (this.latestFinalPayment !== null && this.latestFinalPayment.status === PaymentStatus.REQUEST_REJECTED) {
        return this.paymentStore.updateFinalPayment(payment).finally(() => {
          this.addPaymentEnabled = true;
        });
      } else {
        return this.paymentStore.addPayment(payment).finally(() => {
          this.addPaymentEnabled = true;
        });
      }
    }).map(() => {
      this.resetPaymentForm();
      const eventId = this.onyxEvent.eventId;

      // firing off both calls because both have impacts elsewhere:
      //   CommissionComponent and PaymentEquationComponent
      this.paymentStore.loadPayments(eventId).subscribe();
      this.paymentStore.loadLatestFinalPayment(eventId).subscribe();
      this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
      this.eventStore.getEvent(this.onyxEvent.eventId).subscribe();
    });

    obs.subscribe();
    return obs;
  }

  isAddPaymentEnabled(): Observable<boolean> {
    return this.canAddPayment().map((canAddPayment: boolean) => {
      return canAddPayment && this.paymentForm.valid;
    });
  }

  canAddPayment(): Observable<boolean> {
    return this.calculatedPayment.flatMap((currentCalculatedPayment) => {
      return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
        return this.paymentStore.isCurrentPaymentValid.map(currentPaymentValid => {
          return this.addPaymentEnabled &&
            currentCalculatedPayment > 0 &&
            !currentPaymentValid &&
            this.isValidPaymentDate() &&
            EventUtils.doesEventHaveAgency(event);
        });
      });
    });
  }

  resetPaymentForm(): void {
    this.paymentForm.reset({
      description: '',
      type: null,
      commissionAmount: undefined
    });
  }

  confirmBeforeAddPaymentHandler(value) {
    if (value) {
      this.savePayment();
    }
  }

  public get calculatedPayment(): Observable<number> {
    return this.adjustmentStore.getCalculatedPayment(ReservationStatusCode.CHECKED_OUT, this.onyxEvent.eventProperty.subscriber)
      .map((calculatedPayment) => {
        let total = calculatedPayment;
        if (this.prepayment && this.prepayment.commissionAmount) {
          total -= this.prepayment.commissionAmount;
        }
        return total;
      });
  }

  public isAuthorizedPayment() {
    return this.authenticationStore.hasPermission('AuthorizePayment') && this.isEventControlledByThisUserType();
  }

  public disablePaymentDescription() {
    return !this.isAuthorizedPayment() && !this.canCancelEvent();
  }

  public isEventControlledByThisUserType() {
    return (this.authenticationStore.isAgencyUser() && this.onyxEvent.isAgencyControlled()) ||
        (this.isPropertyUser && this.onyxEvent.isPropertyControlled());
  }

  public isEditAutoPayEnabled() {
    return this.authenticationStore.hasPermission('EditAutoPay') &&
        this.isPropertyUser && this.onyxEvent.eventProperty.subscriber;
  }

  public isAutopayOn() {
    return this.onyxEvent.autoPayment;
  }

  checkEventDates() {
    this.isEventFinished = DateUtil.differenceInCalendarDays(DateUtil.parseDate(this.onyxEvent.endDate), new Date()) < 0;
  }

  public showRequestPayment(): boolean {
    return (this.authenticationStore.isAgencyUser() && !this.onyxEvent.eventProperty.subscriber);
  }

  public disableAuthorizePayment() {
    return this.isAddPaymentEnabled().map((isAddPaymentEnabled) => {
      return this.isAutopayOn() || !this.isAuthorizedPayment() || !isAddPaymentEnabled;
    });
  }

  private retrieveFinalPaymentHint() {

    this.isAddPaymentEnabled().subscribe((isAddPayEnabled: boolean) => {

      if (this.isAutopayOn()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.AUTO_PAY_ENABLED');
      } else if (this.isAuthorizedPayment() && isAddPayEnabled) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENTS_WILL_BE_PROCESSED');
      } else if (this.isAuthorizedPayment() && !isAddPayEnabled && !this.isEventFinished && !this.showRequestPayment()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_OPTION_DISABLED');
      } else if (this.isAuthorizedPayment() && !isAddPayEnabled && !this.isEventFinished && this.showRequestPayment() ) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_AFTER_EVENT_END_DATE');
      } else if (!this.isAuthorizedPayment()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_OPTION_NOT_AUTHORIZED');
      } else {
        this.finalPaymentHint = null;
      }

    });

  }

  showPaymentForm(): Observable<boolean> {
    return this.canAddPayment().map((canAddPayment: boolean) => {
      let showForm = !this.isAutopayOn() && this.isAuthorizedPayment() && canAddPayment;
      showForm = showForm || this.canCancelEvent();
      showForm = showForm || !this.hasPayment;
      if (!showForm) {
        this.unSelectNonCommType();
      }
      return showForm;
    });
  }

  canCancelEvent(): boolean {
    if (this.isPropertyUser) {
      return !this.isEventFinished;
    } else {
      return !this.isEventFinished && !this.isPropertySubscribed;
    }
  }

  onlyPaidDirect(): boolean {
    return this.isLastPaymentRequestedOrRequestResentOrRequestRejected;
  }

  showPaidDirectCancel(): boolean {
    if ((this.canCancelEvent() || !this.isPropertyUser) && !this.hasPayment) {
      return true;
    }
    return false;
  }

  toggleMasterBillPaidSetting() {

    const updatedModel = this.onyxEvent.merge({
      masterBillPaid: !this.onyxEvent.masterBillPaid,
    }) as OnyxEvent;

    this.eventStore.updateEventIndicators(updatedModel).subscribe();
  }

  addNonCommissionDueTypes(): void {
    this.nonCommissionTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT),
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  addCancelTypes(): void {
    this.cancelTypes = [
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  addPaidDirectTypes(): void {
    this.paidDirectTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT)
    ];
  }

  isPayDirect(): boolean {
    return this.nonCommType && this.nonCommType.key === PaymentType.PAID_DIRECT;
  }

  get nonCommType(): DropdownItem {
    return this.getFormControlValue('type');
  }

  get cancelType(): DropdownItem {
    return this.getFormControlValue('cancelType');
  }

  get nonCommAmountPaid(): number {
    return this.getFormControlValue('commissionAmount');
  }

  private getFormControlValue(name: string): any {
    return this.paymentForm ? this.paymentForm.get(name).value : null;
  }

  isNonCommTypeSelected(): boolean {
    return this.nonCommType != null;
  }

  isCancelTypeSelected(): boolean {
    return this.cancelType != null;
  }

  unSelectNonCommType(): void {
    this.paymentForm.reset({
      description: this.paymentForm.get('description').value,
      type: null,
      cancelType: null,
      commissionAmount: null
    });
  }

  public saveNonCommPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.nonCommType.key;
    const commAmount = this.nonCommAmountPaid;

    const payment = new Payment({
      commissionAmount: commAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      type: paymentType
    });

    const obs$ = this.modifyPayment(payment).pipe(
      tap(() => {
        this.resetPaymentForm();
        const eventId = this.onyxEvent.eventId;

        this.paymentStore.loadPayments(eventId).subscribe();
        this.paymentStore.loadLatestFinalPayment(eventId).subscribe();
        this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
        this.eventStore.getEvent(this.onyxEvent.eventId).subscribe();
      }),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  public saveCancelPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.cancelType.key;
    const commAmount = null;

    const payment = new Payment({
      commissionAmount: commAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      type: paymentType
    });

    const obs$ = this.modifyCancelPayment(payment).pipe(
      tap(() => {
        this.resetPaymentForm();
        const eventId = this.onyxEvent.eventId;

        this.paymentStore.loadPayments(eventId).subscribe();
        this.paymentStore.loadLatestFinalPayment(eventId).subscribe();
        this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
        this.eventStore.getEvent(this.onyxEvent.eventId).subscribe();
      }),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  private modifyPayment(payment: Payment): Observable<any> {
    this.addPaymentEnabled = false;
    if (this.latestFinalPayment && (this.latestFinalPayment.status === PaymentStatus.REQUESTED
          || this.latestFinalPayment.status === PaymentStatus.REQUEST_REJECTED
          || this.latestFinalPayment.status === PaymentStatus.RESENT_REQUEST) ) {
      return this.paymentStore.updateFinalPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    } else {
      return this.paymentStore.addPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    }
  }

  private modifyCancelPayment(payment: Payment): Observable<any> {
    this.addPaymentEnabled = false;
    if (this.latestFinalPayment !== null && this.latestFinalPayment.status === PaymentStatus.REQUEST_REJECTED) {
      return this.paymentStore.cancelExistingPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    } else {
      return this.paymentStore.cancelNewPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    }
  }

  getAmountPaidLabel(): string {
    const label = 'EVENT.COMMISSION.NON_COMM_AMOUNT_PAID';
    const params = {
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    return this.translateService.instant(label, params);
  }

  disablePaidDirect(): boolean {
    let disableButton = false;
    if (this.isPayDirect() && (!this.nonCommAmountPaid || (this.nonCommAmountPaid && this.nonCommAmountPaid < 0))) {
      disableButton = true;
    }
    return disableButton;
  }

  get adjustments(): Observable<List<Adjustment>> {
    return this.adjustmentStore.adjustments;
  }

  areThereAnyAdjustments(): Observable<boolean> {
    return this.adjustments.map(list => list.size > 0);
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/payment/add-payment/add-payment.component.ts